import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { STATUS_META, PRIORITY_META, STATUSES, PRIORITIES } from "../lib/constants";
import { timeAgo } from "../lib/time";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import AttachmentList from "../components/AttachmentList";
import { EmailBody } from "../components/EmailBody";
import { UpcomingRenewalsCard } from "../components/UpcomingRenewalsCard";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Search, Send, Inbox, User, Mail, StickyNote, Inbox as InboxIcon,
  ChevronLeft, ChevronDown, ChevronRight, UserCheck, ClipboardList, Ticket as TicketIcon,
  MessageSquare, AlertTriangle, CheckCircle2, Paperclip, X, Trash2, Sliders, RefreshCw,
} from "lucide-react";
import { uploadAttachment, formatBytes } from "../lib/attachments";

function TicketCard({ t, active, onClick, agents, currentUserId }) {
  const assignee = agents.find((a) => a.id === t.assignee_id);
  const isMine = t.assignee_id === currentUserId;
  return (
    <button
      onClick={onClick}
      data-testid={`agent-ticket-item-${t.id}`}
      aria-current={active ? "true" : "false"}
      className={`relative w-full text-left px-4 py-3.5 border-l-[6px] transition-all ${
        active
          ? "bg-blue-50 border-l-blue-600 shadow-inner ring-2 ring-inset ring-blue-300"
          : "bg-transparent border-l-transparent hover:bg-slate-100/70 hover:border-l-slate-300"
      }`}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-y-2 left-0 -translate-x-1/2 w-1.5 rounded-full bg-blue-600"
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`beacon-dot ${STATUS_META[t.status]?.dot || "bg-slate-400"}`} />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {t.ticket_number}
          </span>
          {isMine && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              mine
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-500 shrink-0">{timeAgo(t.updated_at)}</span>
      </div>
      <div className="mt-1.5 font-semibold text-[15px] text-slate-900 line-clamp-1">{t.subject}</div>
      <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
        {t.customer_name} <span className="text-slate-300">·</span> {t.customer_email}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <PriorityBadge priority={t.priority} testid={`ticket-row-priority-${t.id}`} />
        <span className="text-[11px] text-slate-500 truncate max-w-[45%]">
          {assignee ? (
            <span className="inline-flex items-center gap-1">
              <UserCheck className="w-3 h-3" /> {assignee.name.split(" ")[0]}
            </span>
          ) : (
            <span className="italic text-slate-400">unassigned</span>
          )}
        </span>
      </div>
    </button>
  );
}

function QuickTab({ active, onClick, label, count, testid, tone }) {
  // tone: {inactive: 'bg-... text-...', active: 'bg-... text-...', badge: 'bg-... text-...'}
  const inactive = tone?.inactive || "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900";
  const activeCls = tone?.active || "bg-slate-900 text-white border-slate-900";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap border shadow-sm ${
        active ? activeCls : inactive
      }`}
    >
      {label}
      {typeof count === "number" && (
        <span
          className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
            active ? "bg-white/25 text-white" : (tone?.badge || "bg-slate-100 text-slate-600")
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default function AgentWorkspace() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("unassigned"); // unassigned | mine | in_progress | resolved

  // Toggling a tab must CLOSE the currently opened ticket details.
  // Detail view only opens on an explicit ticket click from the list.
  const switchTab = (next) => {
    setTab(next);
    setSelectedId(null);
    setDetail(null);
  };

  const [tabInitialized, setTabInitialized] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [stats, setStats] = useState({});
  const [noteText, setNoteText] = useState("");
  const [postingNote, setPostingNote] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyEmail, setReplyEmail] = useState(true);
  const [replyFiles, setReplyFiles] = useState([]);
  const [postingReply, setPostingReply] = useState(false);
  const [showDetailMobile, setShowDetailMobile] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes, sRes] = await Promise.all([
        api.get("/tickets"),
        api.get("/agents"),
        api.get("/tickets/stats"),
      ]);
      setTickets(tRes.data);
      setAgents(aRes.data.agents);
      setStats(sRes.data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      toast.error("Could not load tickets", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  // Silent background refresh — doesn't toggle the main "loading" flag,
  // preserves the currently opened ticket, and only surfaces errors quietly.
  const silentRefresh = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const [tRes, sRes] = await Promise.all([
        api.get("/tickets"),
        api.get("/tickets/stats"),
      ]);
      setTickets(tRes.data);
      setStats(sRes.data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      if (manual) toast.error("Refresh failed", { description: formatApiError(err) });
    } finally {
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-poll every 20s so new inbound Gmail tickets appear in the list
  // without the user having to switch tabs. Only runs while the tab is
  // visible to avoid wasted requests when the user has moved away.
  useEffect(() => {
    const POLL_MS = 20000;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        silentRefresh();
      }
    };
    const id = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") silentRefresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [silentRefresh]);


  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error("Could not load ticket", { description: formatApiError(err) });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (tab === "unassigned" && t.assignee_id) return false;
      // "Assigned to me" now excludes tickets already moved to In Progress / Resolved,
      // so they show up in their respective tabs instead of two places at once.
      if (tab === "mine" && (t.assignee_id !== user.id || ["in_progress", "resolved", "closed"].includes(t.status))) return false;
      if (tab === "in_progress" && t.status !== "in_progress") return false;
      if (tab === "resolved" && !["resolved", "closed"].includes(t.status)) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (![t.subject, t.customer_name, t.customer_email, t.ticket_number]
          .some((x) => x.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [tickets, tab, priorityFilter, q, user.id]);

  // First-time landing: open Unassigned if there are any waiting, else "Assigned to me".
  // Runs once when tickets first load.
  useEffect(() => {
    if (tabInitialized || loading || tickets.length === 0) return;
    const unassignedCount = tickets.filter((t) => !t.assignee_id).length;
    setTab(unassignedCount > 0 ? "unassigned" : "mine");
    setTabInitialized(true);
  }, [tickets, loading, tabInitialized]);

  // Detail view stays hidden until the user explicitly clicks a ticket.
  // If the currently opened ticket is deleted, close the detail pane.
  useEffect(() => {
    if (selectedId && !tickets.find((t) => t.id === selectedId) && !loading) {
      setSelectedId(null);
      setDetail(null);
    }
  }, [tickets, selectedId, loading]);

  const updateTicket = async (patch) => {
    if (!selectedId) return;
    try {
      const { data } = await api.patch(`/tickets/${selectedId}`, patch);
      // If an agent just reassigned the ticket to someone else they lose
      // access to it — close the detail pane and drop it from their list.
      if (data && data.reassigned_away) {
        setDetail(null);
        setSelectedId(null);
        setTickets((prev) => prev.filter((t) => t.id !== data.id));
        const s = await api.get("/tickets/stats");
        setStats(s.data);
        toast.success("Ticket reassigned", { description: "It's no longer in your queue." });
        return;
      }
      setDetail(data);
      setTickets((prev) => prev.map((t) => (t.id === data.id ? { ...t, ...data } : t)));
      const s = await api.get("/tickets/stats");
      setStats(s.data);
      toast.success("Ticket updated");
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const assignToMe = async () => {
    await updateTicket({ assignee_id: user.id });
    // Move the user to the "Assigned to me" tab so they can immediately see it
    setTab("mine");
  };

  const deleteTicket = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this ticket permanently? Notes, replies and attachments will be removed. This cannot be undone.")) return;
    try {
      await api.delete(`/tickets/${selectedId}`);
      toast.success("Ticket deleted");
      setTickets((prev) => prev.filter((t) => t.id !== selectedId));
      setDetail(null);
      setSelectedId(null);
      const s = await api.get("/tickets/stats");
      setStats(s.data);
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setPostingNote(true);
    try {
      const { data } = await api.post(`/tickets/${selectedId}/notes`, { body: noteText.trim() });
      setDetail((d) => ({ ...d, notes: [...(d.notes || []), data] }));
      setNoteText("");
      toast.success("Internal note added");
    } catch (err) {
      toast.error("Could not add note", { description: formatApiError(err) });
    } finally {
      setPostingNote(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setPostingReply(true);
    try {
      // upload staff attachments first (Bearer token attached via api interceptor)
      const attachment_ids = [];
      for (const f of replyFiles) {
        try {
          const a = await uploadAttachment(selectedId, f);
          attachment_ids.push(a.id);
        } catch (err) {
          toast.error(`Attachment failed: ${f.name}`, { description: formatApiError(err) });
        }
      }
      const { data } = await api.post(`/tickets/${selectedId}/replies`, {
        body: replyText.trim(),
        send_email: replyEmail,
        attachment_ids,
      });
      setDetail((d) => ({ ...d, replies: [...(d.replies || []), data] }));
      setReplyText("");
      setReplyFiles([]);
      // Reply auto-moves the ticket to "In progress" server-side — refresh so
      // the status/badge, tickets list and KPI counters reflect it immediately.
      try {
        const [dRes, tRes, sRes] = await Promise.all([
          api.get(`/tickets/${selectedId}`),
          api.get("/tickets"),
          api.get("/tickets/stats"),
        ]);
        setDetail(dRes.data);
        setTickets(tRes.data);
        setStats(sRes.data);
      } catch (_) { /* non-fatal — reply was already saved */ }
      if (data.email_sent) {
        toast.success("Reply sent", { description: `Emailed ${detail.customer_email}` });
      } else if (replyEmail) {
        toast.warning("Reply saved — email failed", {
          description: data.email_error || "Check SMTP settings.",
        });
      } else {
        toast.success("Reply saved (email not sent)");
      }
    } catch (err) {
      toast.error("Could not send reply", { description: formatApiError(err) });
    } finally {
      setPostingReply(false);
    }
  };

  const addReplyFiles = (list) => {
    const incoming = Array.from(list || []);
    const combined = [...replyFiles];
    for (const f of incoming) {
      if (combined.length >= 5) { toast.error("Up to 5 files per reply"); break; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10 MB`); continue; }
      combined.push(f);
    }
    setReplyFiles(combined);
  };

  const openTicketMobile = (id) => {
    setSelectedId(id);
    setShowDetailMobile(true);
  };

  const tabCounts = useMemo(() => {
    return {
      unassigned: tickets.filter((t) => !t.assignee_id).length,
      mine: tickets.filter((t) => t.assignee_id === user.id && !["in_progress", "resolved", "closed"].includes(t.status)).length,
      in_progress: tickets.filter((t) => t.status === "in_progress").length,
      resolved: tickets.filter((t) => ["resolved", "closed"].includes(t.status)).length,
    };
  }, [tickets, user.id]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Ticket workspace
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Hey {user.name.split(" ")[0]} — {stats.mine || 0} assigned to you, {stats.unassigned || 0} waiting to be picked up.
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { k: "total", label: "Total", tone: "bg-slate-50 border-slate-200 text-slate-700", num: "text-slate-900" },
            { k: "open", label: "Open", tone: "bg-blue-50 border-blue-200 text-blue-800", num: "text-blue-900" },
            { k: "unassigned", label: "Unassigned", tone: "bg-amber-50 border-amber-200 text-amber-800", num: "text-amber-900" },
            { k: "mine", label: "Mine", tone: "bg-emerald-50 border-emerald-200 text-emerald-800", num: "text-emerald-900" },
          ].map((s) => (
            <div
              key={s.k}
              data-testid={`stat-${s.k}`}
              className={`rounded-xl border px-3 py-2 min-w-[76px] shadow-[0_1px_0_rgba(15,23,42,0.02)] ${s.tone}`}
            >
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{s.label}</div>
              <div className={`text-xl font-bold font-display leading-tight ${s.num}`}>{stats[s.k] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming renewals — collapsible, staff heads-up while working tickets */}
      <div className="mb-4">
        <UpcomingRenewalsCard />
      </div>

      {/* Priority filter (mobile-only, above tabs so it's always visible) */}
      <div className="sm:hidden mb-2 flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 shrink-0">Priority</span>
        <select
          className="beacon-input h-8 py-0 text-xs flex-1"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          data-testid="workspace-priority-filter-mobile"
        >
          <option value="all">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
        </select>
      </div>

      {/* Quick tabs */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
        <QuickTab
          active={tab === "unassigned"}
          onClick={() => switchTab("unassigned")}
          label="Unassigned"
          count={tabCounts.unassigned}
          testid="tab-unassigned"
          tone={{
            inactive: "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 hover:text-amber-900",
            active: "bg-amber-600 border-amber-600 text-white",
            badge: "bg-white text-amber-800 border border-amber-200",
          }}
        />
        <QuickTab
          active={tab === "mine"}
          onClick={() => switchTab("mine")}
          label="Assigned to me"
          count={tabCounts.mine}
          testid="tab-mine"
          tone={{
            inactive: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900",
            active: "bg-emerald-600 border-emerald-600 text-white",
            badge: "bg-white text-emerald-800 border border-emerald-200",
          }}
        />
        <QuickTab
          active={tab === "in_progress"}
          onClick={() => switchTab("in_progress")}
          label="In progress"
          count={tabCounts.in_progress}
          testid="tab-in-progress"
          tone={{
            inactive: "bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100 hover:text-blue-900",
            active: "bg-blue-600 border-blue-600 text-white",
            badge: "bg-white text-blue-800 border border-blue-200",
          }}
        />
        <QuickTab
          active={tab === "resolved"}
          onClick={() => switchTab("resolved")}
          label="Resolved"
          count={tabCounts.resolved}
          testid="tab-resolved"
          tone={{
            inactive: "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900",
            active: "bg-slate-800 border-slate-800 text-white",
            badge: "bg-white text-slate-700 border border-slate-200",
          }}
        />
        <div className="flex-1 min-w-4" />
        <button
          type="button"
          onClick={() => silentRefresh({ manual: true })}
          disabled={refreshing}
          data-testid="workspace-refresh-btn"
          title={lastRefreshedAt ? `Last refreshed ${timeAgo(lastRefreshedAt.toISOString())}` : "Refresh now"}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 hover:border-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        <select
          className="hidden sm:block beacon-input h-8 py-0 text-xs w-auto"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          data-testid="workspace-priority-filter"
        >
          <option value="all">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
        </select>
      </div>

      {/* Two-pane */}
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-16rem)] min-h-[520px]">
        {/* Left: list */}
        <div
          className={`w-full lg:w-[380px] beacon-card overflow-hidden flex flex-col ${
            showDetailMobile ? "hidden lg:flex" : "flex"
          }`}
          data-testid="agent-workspace-ticket-list"
        >
          <div className="p-3 border-b border-slate-200 bg-slate-50/70">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="workspace-search-input"
                placeholder="Search subject, name, email, ticket #..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-9 bg-white"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="p-6 text-sm text-slate-500 text-center">Loading tickets...</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto">
                  <InboxIcon className="w-7 h-7 text-slate-400" />
                </div>
                <div className="mt-3 text-sm font-semibold text-slate-700">
                  {tickets.length === 0 ? "No tickets yet" : "No tickets in this view"}
                </div>
                <div className="text-xs text-slate-500 mt-1 max-w-[240px] mx-auto">
                  {tickets.length === 0
                    ? "New customer tickets will show up here the moment they're submitted."
                    : "Try switching tabs or clearing the search to see more."}
                </div>
                {tickets.length === 0 && (
                  <a
                    href="/submit"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-blue-700 hover:text-blue-800"
                    data-testid="empty-open-submit-link"
                  >
                    <TicketIcon className="w-3.5 h-3.5" /> Open customer submit page
                  </a>
                )}
              </div>
            ) : (
              filtered.map((t) => (
                <TicketCard
                  key={t.id}
                  t={t}
                  agents={agents}
                  active={selectedId === t.id}
                  currentUserId={user.id}
                  onClick={() => openTicketMobile(t.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: detail */}
        <div
          className={`flex-1 beacon-card overflow-hidden flex-col min-w-0 ${
            showDetailMobile ? "flex" : "hidden lg:flex"
          }`}
          data-testid="agent-workspace-ticket-detail"
        >
          {!detail ? (
            <div className="flex-1 grid place-items-center text-slate-500 text-sm p-10 text-center">
              <div>
                <ClipboardList className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <div className="font-semibold text-slate-700">
                  {detailLoading ? "Loading ticket..." : "Pick a ticket to open it"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Select a ticket from the list on the left.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Sticky mobile-only back bar — keeps escape hatch + context visible while scrolling */}
              <div className="lg:hidden sticky top-0 z-20 flex items-center gap-2 px-3 py-2 bg-white/95 backdrop-blur border-b border-slate-200"
                data-testid="mobile-detail-topbar">
                <button
                  onClick={() => setShowDetailMobile(false)}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 shrink-0"
                  data-testid="mobile-back-btn"
                  aria-label="Back to ticket list"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <span className="font-mono text-[11px] text-slate-500 truncate">{detail.ticket_number}</span>
                <div className="ml-auto shrink-0"><StatusBadge status={detail.status} /></div>
              </div>

              <div className="p-4 sm:p-6 border-b border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    {/* Ticket # + time — hidden on mobile since sticky bar already shows the number */}
                    <div className="hidden lg:flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                        {detail.ticket_number}
                      </span>
                      <span className="text-[11px] text-slate-400">·</span>
                      <span className="text-[11px] text-slate-500">
                        {timeAgo(detail.created_at)}
                      </span>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-slate-900 font-display leading-snug lg:mt-1">
                      {detail.subject}
                    </h2>
                    <div className="mt-2 text-xs text-slate-500 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-1 sm:gap-x-3 sm:gap-y-1">
                      <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{detail.customer_name}</span>
                      <a
                        href={`mailto:${detail.customer_email}`}
                        className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-800 truncate max-w-full"
                        data-testid="customer-email-link"
                      >
                        <Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{detail.customer_email}</span>
                      </a>
                      <span className="inline-flex items-center gap-1 capitalize"><span className="sm:hidden text-[10px] font-mono uppercase tracking-widest text-slate-400">category</span>{detail.category.replace("_", " ")}</span>
                      <span className="sm:hidden text-[11px] text-slate-400">{timeAgo(detail.created_at)}</span>
                    </div>
                  </div>
                  {/* Badges moved into the collapsible Assignment card below to reduce duplication */}
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {detail.assignee_id !== user.id && (
                    <Button
                      size="sm"
                      onClick={assignToMe}
                      data-testid="assign-to-me-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                    >
                      <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Assign to me
                    </Button>
                  )}
                  {detail.status !== "in_progress" && detail.status !== "resolved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateTicket({ status: "in_progress" })}
                      data-testid="quick-in-progress-btn"
                      className="h-8"
                    >
                      Start working
                    </Button>
                  )}
                  {detail.status !== "resolved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateTicket({ status: "resolved" })}
                      data-testid="quick-resolve-btn"
                      className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      Mark resolved
                    </Button>
                  )}
                  {user.role === "admin" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={deleteTicket}
                      data-testid="delete-ticket-btn"
                      className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 sm:ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                  )}
                </div>

                {/* Assignment controls — collapsible to reduce clutter */}
                {(() => {
                  const assignee = agents.find((a) => a.id === detail.assignee_id);
                  const assigneeLabel = assignee ? assignee.name : "Unassigned";
                  return (
                    <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="assignment-card">
                      <button
                        type="button"
                        onClick={() => setAssignOpen((v) => !v)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
                        data-testid="assignment-toggle-btn"
                        aria-expanded={assignOpen}
                      >
                        <Sliders className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 shrink-0">Assignment</span>
                        <div className="ml-2 flex items-center gap-2 flex-wrap min-w-0">
                          <StatusBadge status={detail.status} />
                          <PriorityBadge priority={detail.priority} />
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${assignee ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                            <User className="w-3 h-3" /> {assigneeLabel}
                          </span>
                        </div>
                        <div className="ml-auto shrink-0">
                          {assignOpen
                            ? <ChevronDown className="w-4 h-4 text-slate-400" />
                            : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </div>
                      </button>
                      {assignOpen && (
                        <div className="p-3 grid sm:grid-cols-3 gap-2 border-t border-slate-100 bg-slate-50/40">
                          <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Status</div>
                            <select
                              className="beacon-input"
                              data-testid="agent-ticket-status-select"
                              value={detail.status}
                              onChange={(e) => updateTicket({ status: e.target.value })}
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                            </select>
                          </div>
                          <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Priority</div>
                            <select
                              className="beacon-input"
                              data-testid="agent-ticket-priority-select"
                              value={detail.priority}
                              onChange={(e) => updateTicket({ priority: e.target.value })}
                            >
                              {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                            </select>
                          </div>
                          <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Assignee</div>
                            <select
                              className="beacon-input"
                              data-testid="agent-ticket-assignee-select"
                              value={detail.assignee_id || ""}
                              onChange={(e) => updateTicket({ assignee_id: e.target.value })}
                            >
                              <option value="">Unassigned</option>
                              {agents.map((a) => (
                                <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-5 bg-slate-50/40">
                <div className="beacon-card p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      Customer message
                    </div>
                    <span className="text-[10px] text-slate-400">{timeAgo(detail.created_at)}</span>
                  </div>
                  <div className="mt-2">
                    <EmailBody text={detail.message} html={detail.message_html} />
                  </div>
                  {(detail.attachments || []).length > 0 && (
                    <div className="mt-4">
                      <AttachmentList
                        attachments={detail.attachments}
                        token={localStorage.getItem("beacon_token")}
                        testid="ticket-attachments"
                      />
                    </div>
                  )}
                </div>

                {/* Public reply thread */}
                <div className="beacon-card p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-blue-700 font-bold">
                      Replies to customer
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(detail.replies || []).length === 0 && (
                      <div className="text-xs text-slate-500 italic">
                        No replies yet. Compose one below — it will be emailed to {detail.customer_email}.
                      </div>
                    )}
                    {(detail.replies || []).map((r) => (
                      <div
                        key={r.id}
                        className="bg-blue-50/60 border border-blue-100 rounded-lg p-3"
                        data-testid={`ticket-reply-${r.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-[11px] text-blue-800 font-semibold">
                            {r.author_name}
                            <span className="text-blue-700/70 font-normal"> · {timeAgo(r.created_at)}</span>
                          </div>
                          {r.email_sent ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                              <CheckCircle2 className="w-3 h-3" /> emailed
                            </span>
                          ) : r.email_error ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5"
                              title={r.email_error}>
                              <AlertTriangle className="w-3 h-3" /> email failed
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                              not emailed
                            </span>
                          )}
                        </div>
                        <div className="mt-1">
                          <EmailBody text={r.body} html={r.body_html} />
                        </div>
                        {(r.attachments || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {r.attachments.map((a) => (
                              <a
                                key={a.id}
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}?auth=${encodeURIComponent(localStorage.getItem("beacon_token") || "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs bg-white border border-blue-200 hover:border-blue-500 rounded-md px-2 py-1 text-blue-700"
                                data-testid={`reply-attachment-${a.id}`}
                              >
                                <Paperclip className="w-3 h-3" /> {a.filename}
                                <span className="text-slate-400">({formatBytes(a.size)})</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 space-y-2">
                    <textarea
                      rows={4}
                      className="beacon-input"
                      placeholder={`Reply to ${detail.customer_name}...`}
                      data-testid="ticket-reply-textarea"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                    />

                    {replyFiles.length > 0 && (
                      <ul className="space-y-1" data-testid="reply-attach-list">
                        {replyFiles.map((f, i) => {
                          const fileKey = `${f.name}-${f.size}-${f.lastModified}`;
                          return (
                          <li key={fileKey} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
                            <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                            <span className="flex-1 truncate">{f.name}</span>
                            <span className="text-slate-400">{formatBytes(f.size)}</span>
                            <button type="button" onClick={() => setReplyFiles(replyFiles.filter((_, j) => j !== i))}
                              className="text-slate-400 hover:text-rose-600 p-0.5"
                              data-testid={`reply-attach-remove-${i}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-4 flex-wrap">
                        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
                          <Paperclip className="w-3.5 h-3.5" />
                          <span>Attach files</span>
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            accept="image/*,application/pdf,text/plain,.log,.zip"
                            data-testid="reply-attach-input"
                            onChange={(e) => { addReplyFiles(e.target.files); e.target.value = ""; }}
                          />
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={replyEmail}
                            onChange={(e) => setReplyEmail(e.target.checked)}
                            data-testid="ticket-reply-email-toggle"
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="sm:hidden">Email customer</span>
                          <span className="hidden sm:inline">Email to <span className="font-mono">{detail.customer_email}</span></span>
                        </label>
                      </div>
                      <Button
                        onClick={sendReply}
                        disabled={postingReply || !replyText.trim()}
                        data-testid="ticket-reply-submit-btn"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg w-full sm:w-auto"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {postingReply ? "Sending..." : replyEmail ? "Send reply" : "Save reply"}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-amber-50/70 border border-amber-200/90 p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <StickyNote className="w-4 h-4 text-amber-700" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-amber-800 font-bold">
                      Internal notes · staff only
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(detail.notes || []).length === 0 && (
                      <div className="text-xs text-amber-900/60 italic">
                        No internal notes yet. Drop a private update for your team below.
                      </div>
                    )}
                    {(detail.notes || []).map((n) => (
                      <div
                        key={n.id}
                        className="bg-white border border-amber-200 rounded-lg p-3"
                        data-testid={`internal-note-${n.id}`}
                      >
                        <div className="text-[11px] text-amber-800 font-semibold">
                          {n.author_name}
                          <span className="text-amber-700/70 font-normal"> · {timeAgo(n.created_at)}</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{n.body}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 space-y-2">
                    <textarea
                      rows={3}
                      className="beacon-input bg-white border-amber-300 focus:ring-amber-200 focus:border-amber-500"
                      placeholder="Add a private note visible only to your team..."
                      data-testid="internal-note-textarea"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                    />
                    <Button
                      onClick={addNote}
                      disabled={postingNote || !noteText.trim()}
                      data-testid="internal-note-submit-btn"
                      className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg w-full sm:w-auto"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {postingNote ? "Posting..." : "Add internal note"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
